import { useCallback, useContext, useMemo, useState } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { ToggleSelectionAllItemsAction, UpdateFilterAction } from '../../../lib/context/SmartSendContextModels';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { EventSections } from '../../../lib/events/EventLogger';
import {
  SmartSendFilter,
  SmartSendFilterContext,
  SmartSendSummary,
  SortField
} from '../../../lib/helpers/SmartSendFilterApplier';
import { EditType } from '../item/EditType';
import SmartSendFilterBar from './SmartSendFilterBar';
import SmartSendFilterSideBar from './SmartSendFilterSideBar';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { P2GFade } from '@parcel2go/core-ui';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props {
  filterKey: string;
  filter: SmartSendFilterContext;
  summary: SmartSendSummary;
  isXs: boolean;
}

const actions: SortField[] = [
  'Product',
  'SKU',
  'Recipient',
  'RecipientName',
  'RecipientPostcode',
  'BuyerId',
  'Country',
  'Source',
  'MarketplaceService',
  'TransactionProcessed',
  'MarketplaceReference',
  'MarketplaceStatus',
  'Date',
  'Service',
  'Status',
  'OrderQuantity',
  'Weight'
];

const marketplaceStates = ['Sold', 'AwaitingDispatch', 'Dispatched'];

const minItemsToShowFilter = 3;

export default function SmartSendFiltering(props: props) {
  const { dispatch } = useContext(SmartSendContext);
  const [openFilter, setOpenFilter] = useState(false);
  const { emit } = useEventEmitter<EditEventType>();
  const { goTo } = useGoto();
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [hasInteracted, setHasInteracted] = useState(false);

  const showFilter: boolean = useMemo(
    () =>
      hasInteracted ||
      (props.filter.CurrentFilter.Name !== 'DEFAULT' && props.filter.CurrentFilter.Name !== 'Issues') ||
      (props.summary.stats.find((s) => s.key === 'Sold')?.value as number) >= minItemsToShowFilter,
    [props.filter.CurrentFilter, props.summary.stats, hasInteracted]
  );

  const log = useCallback(
    (link: string) => {
      track({
        type: 'quick_action_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.QuickActions]),
          action: link
        }
      });
    },
    [track]
  );

  const setAction = useCallback(
    (a: EditType) => {
      switch (a) {
        case 'CSV':
          log('upload-csv');
          goTo(RoutingConstants.SmartSend);
          break;
        case 'CREATE':
          log('add-manual-item');
          goTo(RoutingConstants.SmartSend);
          break;
        case 'RESTORE':
          log('restore-item');
          goTo(RoutingConstants.SmartSend);
          break;
      }

      emit(Consts.Edit, { Id: null, Action: a });
      setHasInteracted(true);
    },
    [emit, goTo, log]
  );

  const update = useCallback(
    (filter: SmartSendFilter) => {
      setHasInteracted(true);
      dispatch(new UpdateFilterAction(props.filterKey, { ...props.filter, CurrentFilter: filter }));
    },
    [dispatch, props.filter, props.filterKey]
  );

  const selectAll = useCallback(() => {
    setHasInteracted(true);
    dispatch(new ToggleSelectionAllItemsAction(props.filterKey, true));
  }, [dispatch, props.filterKey]);

  const onCloseFilter = useCallback(() => {
    log('filter');
    setOpenFilter(false);
  }, [log]);

  const onOpenFilter = useCallback(() => {
    log('filter');
    setOpenFilter(true);
  }, [log]);

  if (!showFilter) {
    return null;
  }

  return (
    <>
      <P2GFade in timeout={1000}>
        <SmartSendFilterBar
          actions={actions}
          marketplaceStates={marketplaceStates}
          update={update}
          openFilter={onOpenFilter}
          openSortBy={() => log('sort')}
          filter={props.filter.CurrentFilter}
          summary={props.summary}
          selectAll={selectAll}
          isXs={props.isXs}
          setAction={setAction}
          filterKey={props.filterKey}
        />
      </P2GFade>
      <SmartSendFilterSideBar
        title={t('FILTER_ORDERS_SIDEPANEL_TITLE', { defaultValue: 'Filter Orders' })}
        actions={actions}
        filter={props.filter}
        open={openFilter}
        close={onCloseFilter}
        update={update}
        count={props.summary.count}
        isXs={props.isXs}
        filterKey={props.filterKey}
      />
    </>
  );
}
